import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { NgForOf, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ActivatedRoute, Router } from '@angular/router';

import { SnCamera, SnLayer, SnLocation } from '@lib/api/entities';
import { SnCollection } from '@lib/common/models';
import { TranslationModule } from '@lib/translation/translation.module';
import { SnDataListModule } from '@lib/ui/data-list';
import { SnDialogService } from '@lib/ui/dialog';
import { SnIconModule } from '@lib/ui/icon';
import { SnAbilityService } from 'libs/ability/src/lib/services';

import { AddInheritPolicyDialogComponent } from '../../components/add-inherit-policy-dialog/add-inherit-policy-dialog.component';
import { AddPolicyDialogComponent } from '../../components/add-policy-dialog/add-policy-dialog.component';
import { FsPolicy } from '../../models/policy';
import { FsPolicyGroup } from '../../models/policy-group';
import { PolicyGroupsModule } from '../../policy-groups.module';
import { FsPolicyGroupService } from '../../services';
import { AxonManagedPermissionMessageComponent } from '../axon-managed-permission-message/axon-managed-permission-message.component';

@Component({
  selector: 'fs-manage-policy-group',
  templateUrl: './manage-policy-group.container.html',
  styleUrls: ['./manage-policy-group.container.scss'],
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatProgressBarModule,
    SnDataListModule,
    MatExpansionModule,
    PolicyGroupsModule,
    SnIconModule,
    MatInputModule,
    FormsModule,
    NgIf,
    NgForOf,
    MatButtonModule,
    AxonManagedPermissionMessageComponent,
    TranslationModule,
  ],
})
export class ManagePolicyGroupContainer implements OnInit, OnDestroy {
  public group: FsPolicyGroup = new FsPolicyGroup();
  public isLoading: boolean = true;
  public cameras = new SnCollection<SnCamera>();
  public locations = new SnCollection<SnLayer>();
  public layers = new SnCollection<SnLayer>();

  private _destroy$ = new Subject<FsPolicyGroup>();

  constructor(
    private readonly _policyGroupService: FsPolicyGroupService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _router: Router,
    private readonly _dialog: MatDialog,
    private readonly _cd: ChangeDetectorRef,
    private readonly _dialogService: SnDialogService,
    private readonly _toastrService: ToastrService,
    private readonly _ability: SnAbilityService,
  ) {}

  get canChange() {
    if (
      !this._ability.can({
        subject: 'fieldwatch_api_auth/authpolicygroup',
        action: 'change',
      })
    ) {
      return false;
    }

    if (this.group.isExternallyManaged) {
      return this.group.canEditPermissions || this.group.canEditMembers;
    }

    return true;
  }

  get canEditPermissions() {
    return this.group.canEditPermissions && this.canChange;
  }

  get canEditMembers() {
    return this.group.canEditMembers && this.canChange;
  }

  public ngOnInit(): void {
    const id = this._activatedRoute.snapshot.params.id;
    this.isLoading = true;
    this._policyGroupService
      .get(id)
      .pipe(takeUntil(this._destroy$))
      .subscribe((group: FsPolicyGroup) => {
        this.group = group;
        this.isLoading = false;
        this._cd.detectChanges();
      });
  }

  public set(policy: FsPolicy, key: string, objects: any): void {
    policy[key] = objects;
  }

  public addPolicy(): void {
    const dialogRef = this._dialog.open(AddPolicyDialogComponent, {
      width: '700px',
      maxWidth: '90%',
      data: {
        policy: new FsPolicy(),
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result) => !!result),
        takeUntil(this._destroy$),
      )
      .subscribe((result) => {
        this.group.policies.push(result.policy);
        this._cd.detectChanges();
      });
  }

  public editPolicy(data: FsPolicy): void {
    const dialogRef = this._dialog.open(AddPolicyDialogComponent, {
      width: '700px',
      maxWidth: '90%',
      data: { policy: data },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result) => !!result),
        takeUntil(this._destroy$),
      )
      .subscribe((result) => {
        this._cd.detectChanges();
      });
  }

  public removePolicy(index: number): void {
    this._dialogService
      .confirm({
        title: 'Confirm',
        message: 'Are you sure that you want to remove this policy?',
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.group.policies.splice(index, 1);
        this._cd.detectChanges();
      });
  }

  public save(): void {
    this.isLoading = true;
    this._policyGroupService
      .update(this.group.id, this.group)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.isLoading = false;
        this._toastrService.success('Permission group updated');
        this._cd.detectChanges();
      });
  }

  public back(): void {
    this._router.navigate(['..'], {
      relativeTo: this._activatedRoute,
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public inheritPolicies(): void {
    this._dialog
      .open(AddInheritPolicyDialogComponent)
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((policies) => {
        if (policies) {
          this.group.policies.push(...policies);
          this._cd.detectChanges();
        }
      });
  }
}
